import { diff } from "deep-diff";
import { createSelector } from "reselect";
import createCachedSelector from "re-reselect";
import { type SortDirection, type TabularColumnConfig, type TabularViewData } from "../models";
import { type SearchPageState } from "./types";
import { type FeatureData, type SearchResultRow } from "../../utils/models";
import { type RootState } from "../../redux/store";
import { toMapFn } from "../../utils/reducers";
import { type ProductData } from "../../models/ProductData";
import { convert, roundWeight, WeightUnit } from "../../utils/WeightUtils";
import { NumberFormatter as NF } from "../../utils/NumberFormatter";
import { createDeepEqualSelector } from "./createDeepEqualSelector";
import { createTabularViewConfiguration } from "../utils/searchUtils";
import { UnitMode } from "../../ImperialMetricData/redux/UnitMode";
import { NUMBER_OF_BOLTS_CODE } from "~/HousingSelectionAssistant/constants";
import { isTechValueFilterCategory, type TechFilter } from "~/Search/filterConfigModels";
import type { TabularColumnDefDTO } from "~/Search/dto/SearchResultsDTO";

type DataMap = { [title: string]: number | undefined };

export const getIsApplyEnabled = createSelector(
  (state: RootState) => state.srp.rawTechFilterValues,
  (state: RootState) => state.srp.appliedTechFilterValues,
  (rawTechFilterValues, appliedTechFilterValues) => !!diff(rawTechFilterValues, appliedTechFilterValues) || false,
);

const getTechValueFilters = (state: RootState, techValueTitle: string | string[]): TechFilter[] | undefined => {
  const config = selectActiveFilterConfiguration(state);
  if (config) {
    if (typeof techValueTitle === "string") {
      techValueTitle = [techValueTitle];
    }
    const validTitles = toMapFn(
      techValueTitle,
      (title) => title,
      () => true,
    );
    return config
      .filter(isTechValueFilterCategory)
      .flatMap((category) => category.items)
      .filter((filterCategory) => validTitles[filterCategory.title]);
  }
};

export const getIsClearEnabled = createCachedSelector<
  RootState,
  string | string[],
  TechFilter[] | undefined,
  DataMap,
  DataMap,
  boolean
>(
  getTechValueFilters,
  (state: RootState) => state.srp.rawTechFilterValues,
  (state: RootState) => state.srp.appliedTechFilterValues,
  (techValueFilters, rawValues, appliedValues) => {
    if (!techValueFilters) {
      return false;
    }
    const keys = [...Object.keys(rawValues), ...Object.keys(appliedValues)].map((fullCode) => {
      const parts = fullCode.split(".");
      return parts.slice(0, parts.length - 1).join(".");
    });
    return techValueFilters.some((techValueFilter) => {
      return keys.indexOf(techValueFilter.uid) !== -1;
    });
  },
)((state, techValueCategoryTitle) =>
  typeof techValueCategoryTitle === "string" ? techValueCategoryTitle : techValueCategoryTitle.join("-"),
);

export const getIsClearEnabledForFilters = createCachedSelector<
  RootState,
  string | string[],
  string[],
  DataMap,
  DataMap,
  boolean
>(
  (state, codes) => (Array.isArray(codes) ? codes : [codes]),
  (state: RootState) => state.srp.rawTechFilterValues,
  (state: RootState) => state.srp.appliedTechFilterValues,
  (techFilterUids, rawValues, appliedValues) => {
    if (!techFilterUids.length) {
      return false;
    }
    const keys = [...Object.keys(rawValues), ...Object.keys(appliedValues)].map((fullCode) => {
      const parts = fullCode.split(".");
      return parts.slice(0, parts.length - 1).join(".");
    });
    return techFilterUids.some((uid) => {
      return keys.indexOf(uid) !== -1;
    });
  },
)((state, techValueCategoryTitle) =>
  typeof techValueCategoryTitle === "string" ? techValueCategoryTitle : techValueCategoryTitle.join("-"),
);

export const selectHasNonDefaultSort = (state: RootState) => {
  const { availableSorts, selectedCode } = state.srp.sort;
  return availableSorts[0] && selectedCode !== availableSorts[0].code;
};

export const selectBeSortCodeNested = (state: SearchPageState) => {
  const { selectedCode, selectedDirection } = state.sort;
  return getBESortCode(selectedCode, selectedDirection);
};

export const getBESortCode = (selectedCode: string | null, selectedDirection: SortDirection | null) => {
  if (!selectedCode) {
    return null;
  }
  if (selectedDirection) {
    return selectedCode + selectedDirection;
  }
  return selectedCode;
};

export const selectBeSortCode = (state: RootState) => selectBeSortCodeNested(state.srp);

export function selectActiveFilterConfiguration(state: RootState) {
  if (state.imperialMetricData.unitMode === UnitMode.Metric) {
    return state.srp.filterConfig;
  } else {
    return state.srp.filterConfigImperial;
  }
}

export const selectActiveTabularViewConfiguration = createCachedSelector<
  RootState,
  TabularColumnDefDTO[] | null,
  TabularColumnConfig[]
>(
  (state) => {
    if (state.imperialMetricData.unitMode === UnitMode.Metric) {
      return state.srp.tabularViewColConfig; //metric filter config
    } else {
      return state.srp.tabularViewColConfigImperial; //imperial filter config
    }
  },
  (tabularViewCols) => {
    if (!tabularViewCols) {
      return [];
    }
    return createTabularViewConfiguration(tabularViewCols);
  },
)((state, productGroupCode) => state.imperialMetricData.unitMode);

export function getProductGroupCodeForFilterConfig(fullProductGroupCode: string | undefined) {
  if (!fullProductGroupCode) {
    return "";
  }
  if (fullProductGroupCode.startsWith("/")) {
    fullProductGroupCode = fullProductGroupCode.slice(1);
  }
  return fullProductGroupCode.split("/")[0];
}

export const selectProductGroupCountMap = createSelector(
  (state: RootState) => state.srp.facetValues["categoryPath"],
  (facetData) =>
    facetData
      ? facetData.values
          .filter((facetValue) => facetValue.count > 0)
          .reduce(
            (map, facetValue) => {
              map[facetValue.code] = facetValue.count;
              return map;
            },
            {} as Record<string, number>,
          )
      : {},
);

export const selectActiveProductGroups = (state: RootState) => state.srp.selectedProductGroups;

export const selectFacetsWithResultsMap = createDeepEqualSelector(
  (state: RootState) => state.srp.facetValues,
  (facetMap) =>
    Object.values(facetMap).reduce(
      (map, facet) => {
        map[facet.code] = true;
        return map;
      },
      {} as Record<string, boolean>,
    ),
);

export const selectProductResults = (state: RootState) => state.srp.results;
export const selectProductResultAmount = (state: RootState) => state.srp.pagination.totalNumberOfResults;
export const selectCurrentPage = (state: RootState) => state.srp.currentPage;
export const selectProductPageAmount = (state: RootState) => state.srp.pagination.numberOfPages;

export const selectContentResults = (state: RootState) => state.srp.contentResults;
export const selectContentResultAmount = (state: RootState) => state.srp.contentPagination.totalNumberOfResults;
export const selectContentPageAmount = (state: RootState) => state.srp.contentPagination.numberOfPages;

export const selectIsInFlight = (state: RootState) => state.srp.inFlight;
export const selectIsInit = (state: RootState) => state.srp.init;

export const selectSearchQuery = (state: RootState) => state.srp.text;
export const selectBreadcrumbs = (state: RootState) => state.srp.breadcrumbs;

export const selectTechnicalInfo = (state: RootState) => state.srp.technicalInfo;

export const convertProductsToTabularViewData = (
  results: SearchResultRow[],
  productDict: Dictionary<ProductData>,
): TabularViewData[] => {
  return results.map((row) => {
    const product = productDict[row.productCode];
    const base: TabularViewData = {
      id: row.id,
      weight: product.weight ? numberFormatter.format(roundWeight(convert(product.weight, WeightUnit.kg)).value) : "-",
      weightImperial: product.weightImperial
        ? numberFormatter.format(roundWeight(convert(product.weightImperial, WeightUnit.lbs)).value)
        : "-",
      product,
      variantProducts: row.variantProductCodes.map((code) => productDict[code]),
      techValues: toMapFn(row.features, (feature) => feature.techFilterId, makeTabularViewFeature),
    };
    if (product.numberOfBolts) {
      base.techValues[NUMBER_OF_BOLTS_CODE] = {
        value: product.numberOfBolts,
        abbreviation: "",
        abbreviationAddition: "",
        isConverted: false,
      };
    }

    return base;
  });
};

const numberFormatter = NF.getFormatter();
function makeTabularViewFeature(feature: FeatureData) {
  let value;
  if (!feature.values.length) {
    value = "-";
  } else if (feature.type === "number") {
    value = feature.values.map((v) => numberFormatter.format(+v)).join(",\n");
  } else {
    value = feature.values.join(",\n");
  }
  return {
    value,
    abbreviation: feature.abbreviation ?? "",
    abbreviationAddition: feature.abbreviationAddition ?? "",
    isConverted: feature.isConverted ?? false,
  };
}

export const selectTabularViewData = createSelector(
  selectProductResults,
  (state: RootState) => state.products.basic,
  (results, productMap) => {
    return convertProductsToTabularViewData(results, productMap);
  },
);

export const selectVisibleTabularViewColumns = createSelector(
  selectActiveTabularViewConfiguration,
  selectFacetsWithResultsMap,
  (colConfigList, visibilityMap) => {
    if (!colConfigList) {
      return null;
    }
    return colConfigList.filter((col) => visibilityMap[col.techValueUID]);
  },
);

export const selectIsFilterActive = (state: RootState) => state.srp.showOnlyCampaignResults;
export const selectIsRelevantFilterActive = (state: RootState) => state.srp.showOnlyRelevant;

export const selectSearchDisplayMode = (state: RootState) => state.srp.resultsViewMode;
